import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTimeout } from 'react-use'
import { Description, Dots, Status, VStack } from '@revolut-internal/ui-kit'

import { Markdown } from 'components'
import { apiClient, GetFixedPromptTextGenerationOutState } from 'lib/apiClient'
import { QueryKey } from 'lib/queryClient'
import { usePrompt } from 'pages/AiProcessDebug/providers/PromptProvider'

import { CollapsibleMessageText } from './CollapsibleMessageText'

const INITIAL_DELAY = 500
const POLLING_DELAY = 2000

export const FixedPromptTextGeneration = () => {
  const {
    currentFixedPromptTextGenerationIndex,
    currentFixedPromptTextGeneration,
    fixedPromptTextGenerationsCount,
    onPrevClick,
    onNextClick,
  } = usePrompt()

  const id = currentFixedPromptTextGeneration?.id

  const [isReady] = useTimeout(INITIAL_DELAY)

  const { data } = useQuery({
    enabled: Boolean(id && isReady()),
    queryKey: [QueryKey.getFixedPromptTextGeneration, id],
    queryFn: () => apiClient.getFixedPromptTextGeneration(id!),
    refetchInterval: query => {
      const latestState = query.state.data?.state

      if (
        latestState === GetFixedPromptTextGenerationOutState.Pending ||
        latestState === GetFixedPromptTextGenerationOutState.InProgress
      ) {
        return POLLING_DELAY
      }

      return false
    },
  })

  const element = useMemo(() => {
    switch (data?.state) {
      case GetFixedPromptTextGenerationOutState.Failed:
        return (
          <VStack space="s-8">
            <Status useIcon="ExclamationMarkOutline" color="red">
              Failed
            </Status>
            <Description>
              {data.result.code} - {data.result.message}
            </Description>
          </VStack>
        )

      case GetFixedPromptTextGenerationOutState.Generated:
        return <Markdown chatUiRenderMethod>{data.result.response.text}</Markdown>

      default:
        return <Dots aria-label="Loading" color="grey-tone-50" />
    }
  }, [data])

  if (!id) {
    return null
  }

  return (
    <CollapsibleMessageText
      label="Updated"
      variant={
        data?.state === GetFixedPromptTextGenerationOutState.Generated ? 'accent' : 'grey'
      }
      collapsible
      currentPage={currentFixedPromptTextGenerationIndex}
      totalPages={fixedPromptTextGenerationsCount}
      onPrevClick={onPrevClick}
      onNextClick={onNextClick}
    >
      {element}
    </CollapsibleMessageText>
  )
}
